import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MarketingNav from "@/components/layout/MarketingNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PLAY_STORE_URL } from "@/data/aboutContent";
import { useMarketingAssets } from "@/lib/marketingAssets";

type DiscoverExample = {
  name: string;
  assetKey: string;
  duration: string;
  description: string;
  image: string;
  funding?: string;
  featured?: boolean;
};

const highSchoolPrograms: DiscoverExample[] = [
  {
    name: "Yale Young African Scholars",
    assetKey: "discover.program.yale_young_african_scholars",
    duration: "14 Days",
    description: "Intensive academic and leadership programme for African secondary school students.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/YYAS.jpeg",
    funding: "Need-based support available",
    featured: true,
  },
  {
    name: "Zambia Model UN",
    assetKey: "discover.program.zambia_model_un",
    duration: "3 Days",
    description: "Diplomatic simulation and leadership development through UN-style debates.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/MUN.jpg",
    funding: "Check listing for fees",
  },
  {
    name: "Ashoka Young Scholars",
    assetKey: "discover.program.ashoka_young_scholars",
    duration: "14 Days",
    description: "Liberal arts and sciences summer programme for high school students.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/ask.jpg",
    funding: "See programme site for costs",
  },
  {
    name: "Our Moon Zambia",
    assetKey: "discover.program.our_moon_zambia",
    duration: "18 Months",
    description:
      "Young Leaders programme preparing Zambian secondary students for university and careers, with leadership, academics, and mentorship over 18 months.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/our moon.jpg",
    funding: "See programme details",
  },
];

const undergraduatePrograms: DiscoverExample[] = [
  {
    name: "African Leadership University Scholarship",
    assetKey: "discover.program.alu_scholarship",
    duration: "4 Years",
    description:
      "Scholarship pathway toward leadership-focused university study, listed on Discover for eligible high school graduates.",
    image: "https://www.alueducation.com/wp-content/uploads/2024/06/cov-graduation.jpg",
    funding: "Scholarship",
    featured: true,
  },
  {
    name: "Egyptian Government Scholarship",
    assetKey: "discover.program.egyptian_government_scholarship",
    duration: "5 Years",
    description:
      "Government scholarships for Zambian students in science, engineering, and medical fields. This is an example of what partners list on Discover.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/pexels-george-pak-7972735.jpg",
    funding: "Fully funded (per listing)",
  },
  {
    name: "Moroccan Government Scholarships",
    assetKey: "discover.program.moroccan_government_scholarships",
    duration: "5 Years",
    description:
      "Scholarships for Zambian students to pursue undergraduate studies. Funding and eligibility vary by year.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/pexels-olia-danilevich-8093039.jpg",
    funding: "Fully funded (per listing)",
  },
  {
    name: "HELSB-SeedCo Scholarship",
    assetKey: "discover.program.helsb_seedco_scholarship",
    duration: "5 Years",
    description:
      "Scholarships for outstanding Zambian students in agriculture and related fields. Apply through the official process shown in Discover.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/pexels-pavel-danilyuk-7944174.jpg",
    funding: "Scholarship",
  },
];

const ProgramCard = ({
  program,
  level,
  onOpen,
}: {
  program: DiscoverExample;
  level: string;
  onOpen: () => void;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`bg-white rounded-2xl overflow-hidden border ${
      program.featured ? "border-amber-400 ring-2 ring-amber-400/20" : "border-gray-200"
    } shadow-sm hover:shadow-md transition-shadow relative cursor-pointer`}
    onClick={onOpen}
  >
    {program.featured && (
      <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg z-10">
        POPULAR
      </div>
    )}
    <img src={program.image} alt={program.name} className="w-full h-40 object-cover" />
    <div className="p-6">
      <div className="text-xs text-gray-500 mb-2">
        {level} · {program.duration}
      </div>
      <h3 className="font-bold text-gray-900 mb-2">{program.name}</h3>
      <p className="text-sm text-gray-600 mb-4">{program.description}</p>
      {program.funding && <div className="text-xs text-gray-500 mb-4">{program.funding}</div>}
      <span className="text-skutopia-600 font-medium text-sm">Open in app →</span>
    </div>
  </motion.div>
);

const Programs = () => {
  const [appModalOpen, setAppModalOpen] = useState(false);
  const overrides = useMarketingAssets();
  const withOverride = (program: DiscoverExample): DiscoverExample => ({
    ...program,
    image: overrides[program.assetKey]?.url || program.image,
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MarketingNav />

      <div className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-skutopia-600 mb-4"
          >
            Discover examples
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4"
          >
            Opportunities on <span className="text-skutopia-600">Discover</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Skutopia does not run these programmes. Partners publish them; students browse, save, and
            apply in the app. Examples below illustrate what you may find on Discover.
          </motion.p>
        </div>
      </div>

      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              High school
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Programmes for secondary students
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Summer schools, leadership intensives, and enrichment listed by partner organisations.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {highSchoolPrograms.map((program) => (
              <ProgramCard
                key={program.name}
                program={withOverride(program)}
                level="High school"
                onOpen={() => setAppModalOpen(true)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Scholarships & university pathways
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Funding and pathways after high school
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Scholarships and programmes from universities and governments. Skutopia does not grant
              these degrees. Always verify funding and eligibility on the official listing.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {undergraduatePrograms.map((program) => (
              <ProgramCard
                key={program.name}
                program={withOverride(program)}
                level="Scholarship / pathway"
                onOpen={() => setAppModalOpen(true)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="py-12 text-center px-4">
        <p className="text-gray-600 mb-4">Publish opportunities for high school students?</p>
        <Link to="/partners/login" className="text-skutopia-600 font-semibold hover:text-skutopia-700">
          Partner portal →
        </Link>
      </div>

      <AnimatePresence>
        {appModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setAppModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-skutopia-600 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">S</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Open Discover in the app</h3>
                <p className="text-gray-600 mb-6">
                  Browse live opportunities, save favourites, and follow application steps on your phone.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-70">
                    <div className="text-left flex-1">
                      <p className="text-xs text-gray-500">App Store</p>
                      <p className="font-semibold text-gray-900 text-sm">Coming soon</p>
                    </div>
                  </div>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="text-left flex-1">
                      <p className="text-xs text-gray-500">Get it on</p>
                      <p className="font-semibold text-gray-900 text-sm">Google Play</p>
                    </div>
                    <span className="text-skutopia-600">→</span>
                  </a>
                </div>
                <Button
                  onClick={() => setAppModalOpen(false)}
                  className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Programs;
